import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ClarificationFieldsState,
  ClarificationField,
  ClarificationFieldListData,
} from '../../types/clarificationFields.types';

const initialState: ClarificationFieldsState = {
  fields: [],
  selectedField: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
    hasMorePages: false,
  },
  search: '',
  sort: {
    field: 'createdAt',
    order: 'desc',
  },
};

const clarificationFieldsSlice = createSlice({
  name: 'clarificationFields',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFields: (state, action: PayloadAction<ClarificationFieldListData>) => {
      state.fields = action.payload.fields;
      state.pagination = {
        ...state.pagination,
        totalPages: action.payload.totalPages,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loading = false;
      state.error = null;
    },
    setSelectedField: (state, action: PayloadAction<ClarificationField | null>) => {
      state.selectedField = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.pagination.page = 1;
    },
    setSort: (state, action: PayloadAction<{ field: string; order: string }>) => {
      state.sort = action.payload;
    },
    addField: (state, action: PayloadAction<ClarificationField>) => {
      state.fields.unshift(action.payload);
      state.pagination.totalRecords += 1;
    },
    updateField: (state, action: PayloadAction<ClarificationField>) => {
      const index = state.fields.findIndex((f) => f.id === action.payload.id);
      if (index !== -1) {
        state.fields[index] = action.payload;
      }
      if (state.selectedField?.id === action.payload.id) {
        state.selectedField = action.payload;
      }
    },
    removeField: (state, action: PayloadAction<string>) => {
      state.fields = state.fields.filter((f) => f.id !== action.payload);
      state.pagination.totalRecords -= 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedField: (state) => {
      state.selectedField = null;
    },
    resetClarificationFields: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setFields,
  setSelectedField,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addField,
  updateField,
  removeField,
  clearError,
  clearSelectedField,
  resetClarificationFields,
} = clarificationFieldsSlice.actions;

export default clarificationFieldsSlice.reducer;
